import PropTypes from 'prop-types';
import { m, AnimatePresence } from 'framer-motion';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { fData } from 'src/utils/format-number';

import Iconify from '../iconify';
import { varFade } from '../animate';
import FileThumbnail, { fileData } from '../file-thumbnail';

// ----------------------------------------------------------------------

// Shared style for the hover-revealed action buttons on each thumbnail.
// pointerEvents:'auto' re-enables clicks on the buttons; their container
// (.thumb-actions) is pointer-events:none so the action bar never swallows the
// drag-to-reorder gesture.
const ACTION_BTN_SX = {
  p: 0.5,
  color: 'common.white',
  pointerEvents: 'auto',
  bgcolor: (theme) => alpha(theme.palette.grey[900], 0.6),
  '&:hover': { bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8) },
};

export default function MultiFilePreview ({ thumbnail, files = [], onRemove, onEdit, onReorder, sx }) {
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const sourceIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);

    if (sourceIndex === targetIndex) return;

    const items = Array.from(files);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(targetIndex, 0, reorderedItem);

    onReorder?.(items);
  };

  if (!files?.length) {
    return null;
  }

  return (
    <Box
      sx={
        thumbnail
          ? { display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))' }
          : { display: 'flex', flexDirection: 'column' }
      }
    >
      <AnimatePresence initial={false}>
        {files?.map((file, index) => {
          const { key, name = '', size = 0 } = fileData(file);
          const isNotFormatFile = typeof file === 'string';

          if (thumbnail) {
            return (
              <Box
                key={key}
                component={m.div}
                {...varFade().inUp}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                sx={{
                  position: 'relative',
                  width: 1,
                  aspectRatio: '1 / 1',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  cursor: 'grab',
                  border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                  '&:active': {
                    cursor: 'grabbing',
                    boxShadow: (theme) => theme.customShadows?.z20,
                  },
                  // Reveal the edit/remove actions on hover; keep them visible on
                  // touch devices (which have no hover state).
                  '& .thumb-actions': {
                    opacity: 0,
                    transition: (theme) => theme.transitions.create('opacity'),
                  },
                  '&:hover .thumb-actions': { opacity: 1 },
                  '@media (hover: none)': { '& .thumb-actions': { opacity: 1 } },
                  ...sx,
                }}
              >
                <FileThumbnail
                  imageView
                  draggable={false}
                  file={file}
                  sx={{ width: 1, height: 1 }}
                  imgSx={{ position: 'absolute', inset: 0, width: 1, height: 1 }}
                />

                {(onEdit || onRemove) && (
                  <Stack
                    className="thumb-actions"
                    direction="row"
                    justifyContent="flex-end"
                    spacing={0.5}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      p: 0.5,
                      // The bar spans the top of every thumbnail; keep it
                      // transparent to pointer events so it never blocks the
                      // drag-to-reorder gesture. The buttons opt back in via
                      // ACTION_BTN_SX (pointerEvents:'auto').
                      pointerEvents: 'none',
                      background: (theme) =>
                        `linear-gradient(to bottom, ${alpha(theme.palette.grey[900], 0.64)}, transparent)`,
                    }}
                  >
                    {onEdit && (
                      <IconButton size="small" aria-label="Edit image" onClick={(e) => { e.stopPropagation(); onEdit(file, index); }} sx={ACTION_BTN_SX}>
                        <Iconify icon="solar:pen-bold" width={18} />
                      </IconButton>
                    )}
                    {onRemove && (
                      <IconButton size="small" aria-label="Remove image" onClick={(e) => { e.stopPropagation(); onRemove(file); }} sx={ACTION_BTN_SX}>
                        <Iconify icon="mingcute:close-line" width={18} />
                      </IconButton>
                    )}
                  </Stack>
                )}

                {/* The first image is the cover/thumbnail across marketplaces. */}
                {index === 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 6,
                      left: 6,
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 0.75,
                      typography: 'caption',
                      fontWeight: 'fontWeightBold',
                      lineHeight: 1.5,
                      color: 'common.white',
                      bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
                      pointerEvents: 'none',
                    }}
                  >
                    Cover
                  </Box>
                )}
              </Box>
            );
          }

          return (
            <Stack
              key={key}
              component={m.div}
              {...varFade().inUp}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              spacing={2}
              direction="row"
              alignItems="center"
              sx={{
                my: 1,
                py: 1,
                px: 1.5,
                borderRadius: 1,
                border: (theme) => `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`,
                cursor: 'grab',
                '&:active': {
                  cursor: 'grabbing',
                  boxShadow: (theme) => theme.customShadows?.z20,
                },
                ...sx,
              }}
            >
              <FileThumbnail file={file} />

              <ListItemText
                primary={isNotFormatFile ? file : name}
                secondary={isNotFormatFile ? '' : fData(size)}
                secondaryTypographyProps={{
                  component: 'span',
                  typography: 'caption',
                }}
              />

              {onRemove && (
                <IconButton size="small" onClick={() => onRemove(file)}>
                  <Iconify icon="mingcute:close-line" width={16} />
                </IconButton>
              )}
            </Stack>
          );
        })}
      </AnimatePresence>
    </Box>
  );
}

MultiFilePreview.propTypes = {
  files: PropTypes.array,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  onReorder: PropTypes.func,
  sx: PropTypes.object,
  thumbnail: PropTypes.bool,
};
